import { useState } from "react";
import { Icon, cn } from "@nexus/ui";
import { canAnimate } from "../../lib/motion";

export interface AssetThumbnailProps {
  /** Signed preview URL; null falls straight through to the media-type glyph. */
  thumbnailUrl?: string | null;
  /** Signed media URL — paired with `autoPlay` + `isVideo` for inline muted playback. */
  assetUrl?: string | null;
  /** Picks the glyph shown when there is no image to render. */
  isVideo?: boolean;
  /**
   * When true and a video `assetUrl` is available, play muted / looped inline.
   * Honours `prefers-reduced-motion` (falls back to the still thumbnail).
   */
  autoPlay?: boolean;
  iconSize: "xs" | "md";
  className?: string;
  /** Frame sizing that applies only while a thumbnail renders. */
  imageClassName?: string;
  /** Frame sizing for the glyph — read URLs are short-TTL, so they expire mid-session. */
  fallbackClassName?: string;
}

/**
 * Creative preview with a media-type fallback glyph. Signed read URLs expire while a page
 * stays open, so a failed load falls back to the glyph rather than a broken image frame.
 * Large hover panels can opt into muted video autoplay when an asset URL is present.
 */
export function AssetThumbnail({
  thumbnailUrl,
  assetUrl = null,
  isVideo = false,
  autoPlay = false,
  iconSize,
  className,
  imageClassName,
  fallbackClassName,
}: AssetThumbnailProps) {
  const [failedVideoSrc, setFailedVideoSrc] = useState<string | null>(null);
  const [failedImageSrc, setFailedImageSrc] = useState<string | null>(null);

  const videoSrc = assetUrl ?? null;
  const canPlayVideo =
    autoPlay &&
    isVideo &&
    videoSrc != null &&
    videoSrc !== failedVideoSrc &&
    canAnimate();

  const imageSrc = thumbnailUrl ?? null;
  const canShowImage =
    !canPlayVideo && imageSrc != null && imageSrc !== failedImageSrc;

  return (
    <span
      className={cn(
        "bg-muted text-muted-foreground flex items-center justify-center overflow-hidden",
        className,
        canPlayVideo || canShowImage ? imageClassName : fallbackClassName,
      )}
    >
      {canPlayVideo ? (
        <video
          src={videoSrc}
          poster={imageSrc ?? undefined}
          autoPlay
          muted
          loop
          playsInline
          onError={() => setFailedVideoSrc(videoSrc)}
          className="h-full w-full object-cover"
        />
      ) : canShowImage ? (
        <img
          src={imageSrc}
          alt=""
          loading="lazy"
          onError={() => setFailedImageSrc(imageSrc)}
          className="h-full w-full object-cover"
        />
      ) : (
        <Icon
          name={isVideo ? "clapperboard" : "image"}
          size={iconSize}
          aria-hidden
        />
      )}
    </span>
  );
}
